import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api, { formatApiError } from '@/lib/api';
import { Plus, Trash2, Save, ArrowLeft, ArrowUp, ArrowDown, Upload, Download, FileSpreadsheet, HelpCircle, X } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Badge } from '@/components/ui/badge';

const QUESTION_TYPES = [
  { value: 'likert', label: 'Likert Scale' },
  { value: 'multiple_choice', label: 'Multiple Choice (single)' },
  { value: 'multiple_choice_multi', label: 'Multiple Choice (multi)' },
  { value: 'yes_no', label: 'Yes / No' },
  { value: 'text', label: 'Text Input' },
  { value: 'numeric', label: 'Numeric Input' },
];

const RANGE_COLORS = ['green', 'yellow', 'orange', 'red', 'darkred'];
const COLOR_MAP = { green: 'bg-[#2E8B57]/10 text-[#2E8B57]', yellow: 'bg-[#F5B041]/10 text-[#F5B041]', orange: 'bg-[#E67E22]/10 text-[#E67E22]', red: 'bg-[#D3544E]/10 text-[#D3544E]', darkred: 'bg-[#922B21]/10 text-[#922B21]' };

function getDefaults(type) {
  if (type === 'likert') return { options: ['Not at all', 'Several days', 'More than half the days', 'Nearly every day'], scores: [0, 1, 2, 3] };
  if (type === 'yes_no') return { options: ['Yes', 'No'], scores: [1, 0] };
  if (type === 'multiple_choice' || type === 'multiple_choice_multi') return { options: ['Option 1', 'Option 2', 'Option 3'], scores: [0, 1, 2] };
  return { options: [], scores: [] };
}

export default function QuestionnaireBuilderPage() {
  const navigate = useNavigate();
  const { id: editId } = useParams();
  const isEdit = !!editId;
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [scoreVisibility, setScoreVisibility] = useState('PUBLIC');
  const [questions, setQuestions] = useState([]);
  const [scoringRanges, setScoringRanges] = useState([{ min: 0, max: 10, label: 'Not Interpreted', color: 'green' }]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Bulk add state
  const [showBulkAdd, setShowBulkAdd] = useState(false);
  const [bulkCount, setBulkCount] = useState(5);
  const [bulkType, setBulkType] = useState('likert');

  // CSV state
  const [csvPreview, setCsvPreview] = useState(null);
  const [csvError, setCsvError] = useState('');
  const [showCsvPreview, setShowCsvPreview] = useState(false);
  const [showScoringAfterCsv, setShowScoringAfterCsv] = useState(false);

  // Load existing questionnaire for editing
  useEffect(() => {
    if (!editId) return;
    api.get(`/questionnaires/${editId}`).then(({ data }) => {
      setTitle(data.title || '');
      setDescription(data.description || '');
      setScoreVisibility(data.score_visibility || 'PUBLIC');
      setQuestions(data.questions || []);
      if (data.scoring_logic?.ranges?.length) {
        setScoringRanges(data.scoring_logic.ranges.map(r => ({ ...r, color: r.color || 'green' })));
      }
    }).catch(() => setError('Failed to load questionnaire'));
  }, [editId]);

  // Question management
  const addQuestion = (type) => {
    setQuestions(prev => [...prev, { question_text: '', type, ...getDefaults(type) }]);
  };

  const updateQuestion = (idx, field, value) => {
    setQuestions(prev => { const u = [...prev]; u[idx] = { ...u[idx], [field]: value }; return u; });
  };

  const removeQuestion = (idx) => setQuestions(prev => prev.filter((_, i) => i !== idx));

  const moveQuestion = (idx, dir) => {
    const newIdx = idx + dir;
    if (newIdx < 0 || newIdx >= questions.length) return;
    setQuestions(prev => { const u = [...prev]; [u[idx], u[newIdx]] = [u[newIdx], u[idx]]; return u; });
  };

  const updateOption = (qIdx, oIdx, value) => {
    setQuestions(prev => { const u = [...prev]; u[qIdx] = { ...u[qIdx], options: [...u[qIdx].options] }; u[qIdx].options[oIdx] = value; return u; });
  };

  const updateScore = (qIdx, oIdx, value) => {
    setQuestions(prev => { const u = [...prev]; u[qIdx] = { ...u[qIdx], scores: [...u[qIdx].scores] }; u[qIdx].scores[oIdx] = parseInt(value) || 0; return u; });
  };

  const addOption = (qIdx) => {
    setQuestions(prev => { const u = [...prev]; u[qIdx] = { ...u[qIdx], options: [...u[qIdx].options, ''], scores: [...u[qIdx].scores, 0] }; return u; });
  };

  const removeOption = (qIdx, oIdx) => {
    setQuestions(prev => {
      const u = [...prev];
      u[qIdx] = { ...u[qIdx], options: u[qIdx].options.filter((_, i) => i !== oIdx), scores: u[qIdx].scores.filter((_, i) => i !== oIdx) };
      return u;
    });
  };

  // Bulk generation
  const generateBulk = () => {
    const count = Math.min(Math.max(bulkCount, 1), 50);
    const newQs = Array.from({ length: count }, () => ({ question_text: '', type: bulkType, ...getDefaults(bulkType) }));
    setQuestions(prev => [...prev, ...newQs]);
    setShowBulkAdd(false);
  };

  // CSV parsing
  const handleCsvUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const text = ev.target.result;
        const lines = text.split('\n').map(l => l.trim()).filter(l => l && !l.startsWith('question_number'));
        if (lines.length === 0) { setCsvError('CSV is empty'); return; }
        const parsed = [];
        for (let i = 0; i < lines.length; i++) {
          const parts = lines[i].split(',').map(p => p.trim());
          if (parts.length < 3) { setCsvError(`Row ${i + 1}: Expected at least 3 columns`); return; }
          const qNum = parseInt(parts[0]) || i + 1;
          const qText = parts[1] || '';
          const qType = parts[2] || 'likert';
          const validTypes = ['likert', 'multiple_choice', 'multiple_choice_multi', 'yes_no', 'text', 'numeric'];
          if (!validTypes.includes(qType)) { setCsvError(`Row ${i + 1}: Invalid type "${qType}"`); return; }
          const opts = parts[3] ? parts[3].split('|').map(o => o.trim()) : getDefaults(qType).options;
          const scores = parts[4] ? parts[4].split('|').map(s => parseInt(s.trim()) || 0) : getDefaults(qType).scores;
          if (opts.length !== scores.length && opts.length > 0 && scores.length > 0) { setCsvError(`Row ${i + 1}: Options count (${opts.length}) doesn't match scores count (${scores.length})`); return; }
          parsed.push({ num: qNum, question_text: qText, type: qType, options: opts, scores });
        }
        setCsvError('');
        setCsvPreview(parsed);
        setShowCsvPreview(true);
      } catch (err) {
        setCsvError('Failed to parse CSV file');
      }
    };
    reader.readAsText(file);
  };

  const confirmCsvImport = () => {
    if (!csvPreview) return;
    const newQs = csvPreview.map(p => ({ question_text: p.question_text, type: p.type, options: p.options, scores: p.scores }));
    setQuestions(prev => [...prev, ...newQs]);
    setShowCsvPreview(false);
    // Auto-calculate scoring and open interpretation dialog
    const maxScore = newQs.reduce((sum, q) => sum + Math.max(...(q.scores.length ? q.scores : [0])), 0);
    const minScore = newQs.reduce((sum, q) => sum + Math.min(...(q.scores.length ? q.scores : [0])), 0);
    setScoringRanges([{ min: minScore, max: maxScore, label: 'Not Interpreted', color: 'green' }]);
    setShowScoringAfterCsv(true);
    setCsvPreview(null);
  };

  const downloadCsvTemplate = () => {
    const csv = `question_number, question_text, question_type, options, scores
1, Little interest or pleasure in doing things, likert, Not at all|Several days|More than half the days|Nearly every day, 0|1|2|3
2, Feeling down depressed or hopeless, likert, Not at all|Several days|More than half the days|Nearly every day, 0|1|2|3
3, Do you exercise regularly, yes_no, Yes|No, 1|0`;
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'questionnaire_template.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  // Scoring ranges
  const addRange = () => setScoringRanges(prev => [...prev, { min: 0, max: 0, label: '', color: 'green' }]);
  const updateRange = (idx, field, value) => {
    setScoringRanges(prev => { const u = [...prev]; u[idx] = { ...u[idx], [field]: field === 'label' || field === 'color' ? value : parseInt(value) || 0 }; return u; });
  };
  const removeRange = (idx) => setScoringRanges(prev => prev.filter((_, i) => i !== idx));

  // Score calculations
  const maxPossible = questions.reduce((sum, q) => sum + Math.max(...(q.scores?.length ? q.scores : [0])), 0);
  const minPossible = questions.reduce((sum, q) => sum + Math.min(...(q.scores?.length ? q.scores : [0])), 0);

  // Save
  const handleSave = async () => {
    if (!title.trim()) { setError('Title is required'); return; }
    if (questions.length === 0) { setError('Add at least one question'); return; }
    setSaving(true); setError('');
    try {
      const payload = { title, description, questions, scoring_logic: { ranges: scoringRanges }, score_visibility: scoreVisibility };
      if (isEdit) {
        await api.put(`/questionnaires/${editId}`, payload);
      } else {
        await api.post('/questionnaires', payload);
      }
      navigate('/questionnaires');
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  return (
    <div className="max-w-3xl mx-auto space-y-6" data-testid="questionnaire-builder">
      <div className="flex items-center gap-3">
        <Button variant="ghost" onClick={() => navigate('/questionnaires')} data-testid="builder-back-btn"><ArrowLeft className="w-4 h-4" /></Button>
        <h1 className="text-2xl font-semibold text-[#1A3330] font-['Outfit']">{isEdit ? 'Edit Questionnaire' : 'Create Questionnaire'}</h1>
      </div>

      {error && <div className="bg-[#D3544E]/10 text-[#D3544E] p-3 rounded-xl text-sm" data-testid="builder-error">{error}</div>}

      {/* Basic Info */}
      <Card className="bg-white border-[#E5F2F1] rounded-2xl">
        <CardContent className="p-6 space-y-4">
          <div><Label className="text-sm font-medium text-[#1A3330]">Title</Label>
            <Input value={title} onChange={e => setTitle(e.target.value)} placeholder="e.g., PHQ-9, Custom Assessment..." className="rounded-xl border-[#E5F2F1] mt-1" data-testid="builder-title" /></div>
          <div><Label className="text-sm font-medium text-[#1A3330]">Description</Label>
            <Textarea value={description} onChange={e => setDescription(e.target.value)} placeholder="What does this questionnaire measure?" rows={2} className="rounded-xl border-[#E5F2F1] mt-1" data-testid="builder-description" /></div>
          <div>
            <Label className="text-sm font-medium text-[#1A3330]">Score Visibility</Label>
            <div className="flex gap-3 mt-2">
              <button type="button" onClick={() => setScoreVisibility('PUBLIC')}
                className={`flex-1 p-3 rounded-xl border text-left transition-all ${scoreVisibility === 'PUBLIC' ? 'border-[#0D8B7D] bg-[#0D8B7D]/5' : 'border-[#E5F2F1]'}`}
                data-testid="score-visibility-public">
                <div className="flex items-center gap-2">
                  <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${scoreVisibility === 'PUBLIC' ? 'border-[#0D8B7D]' : 'border-[#4A6B68]/30'}`}>
                    {scoreVisibility === 'PUBLIC' && <div className="w-2 h-2 rounded-full bg-[#0D8B7D]" />}
                  </div>
                  <span className="text-sm font-medium text-[#1A3330]">Public Scoring</span>
                </div>
                <p className="text-[10px] text-[#4A6B68] mt-1 ml-6">Score visible to client, therapist, and admin</p>
              </button>
              <button type="button" onClick={() => setScoreVisibility('PRIVATE')}
                className={`flex-1 p-3 rounded-xl border text-left transition-all ${scoreVisibility === 'PRIVATE' ? 'border-[#4A6B68] bg-[#4A6B68]/5' : 'border-[#E5F2F1]'}`}
                data-testid="score-visibility-private">
                <div className="flex items-center gap-2">
                  <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${scoreVisibility === 'PRIVATE' ? 'border-[#4A6B68]' : 'border-[#4A6B68]/30'}`}>
                    {scoreVisibility === 'PRIVATE' && <div className="w-2 h-2 rounded-full bg-[#4A6B68]" />}
                  </div>
                  <span className="text-sm font-medium text-[#1A3330]">Private Scoring</span>
                </div>
                <p className="text-[10px] text-[#4A6B68] mt-1 ml-6">Score visible only to therapist and admin</p>
              </button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Add Questions Actions */}
      <div className="flex flex-wrap items-center gap-3">
        <Button onClick={() => setShowBulkAdd(true)} className="bg-[#0D8B7D] hover:bg-[#0B786C] text-white rounded-xl" data-testid="add-questions-btn">
          <Plus className="w-4 h-4 mr-2" />Add Questions
        </Button>
        <label className="cursor-pointer">
          <Button variant="outline" className="rounded-xl border-[#E5F2F1] text-[#0D8B7D]" asChild data-testid="upload-csv-btn">
            <span><Upload className="w-4 h-4 mr-2" />Upload CSV</span>
          </Button>
          <input type="file" accept=".csv" className="hidden" onChange={handleCsvUpload} />
        </label>
        <Button variant="ghost" onClick={downloadCsvTemplate} className="text-[#4A6B68] text-xs" data-testid="download-template-btn">
          <Download className="w-3.5 h-3.5 mr-1" />Download CSV Template
        </Button>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger><HelpCircle className="w-4 h-4 text-[#4A6B68]" /></TooltipTrigger>
            <TooltipContent className="max-w-sm bg-white border border-[#E5F2F1] text-xs text-[#1A3330] p-3">
              <p className="font-medium mb-1">CSV Format:</p>
              <code className="text-[10px] block bg-[#F4FAF9] p-2 rounded">question_number, question_text, question_type, options, scores</code>
              <p className="mt-1">Types: likert, multiple_choice, multiple_choice_multi, yes_no, text, numeric</p>
              <p>Options &amp; scores separated by | (pipe)</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
      {csvError && <p className="text-sm text-[#D3544E]">{csvError}</p>}

      {/* Questions List */}
      <div className="space-y-4">
        <h2 className="text-lg font-semibold text-[#1A3330] font-['Outfit']">Questions ({questions.length})</h2>

        {questions.map((q, qIdx) => (
          <Card key={qIdx} className="bg-white border-[#E5F2F1] rounded-2xl" data-testid={`question-card-${qIdx}`}>
            <CardContent className="p-5 space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="flex flex-col gap-0.5">
                    <button onClick={() => moveQuestion(qIdx, -1)} disabled={qIdx === 0} className="text-[#4A6B68] hover:text-[#0D8B7D] disabled:opacity-30" data-testid={`move-up-${qIdx}`}><ArrowUp className="w-3 h-3" /></button>
                    <button onClick={() => moveQuestion(qIdx, 1)} disabled={qIdx === questions.length - 1} className="text-[#4A6B68] hover:text-[#0D8B7D] disabled:opacity-30" data-testid={`move-down-${qIdx}`}><ArrowDown className="w-3 h-3" /></button>
                  </div>
                  <span className="text-xs font-semibold text-[#0D8B7D] uppercase tracking-wider">Q{qIdx + 1} · {QUESTION_TYPES.find(t => t.value === q.type)?.label || q.type}</span>
                </div>
                <Button variant="ghost" size="sm" onClick={() => removeQuestion(qIdx)} className="text-[#D3544E] h-7" data-testid={`remove-q-${qIdx}`}><Trash2 className="w-3 h-3" /></Button>
              </div>
              <Input value={q.question_text} onChange={e => updateQuestion(qIdx, 'question_text', e.target.value)} placeholder="Enter question text..." className="rounded-xl border-[#E5F2F1]" data-testid={`question-text-${qIdx}`} />

              {['likert', 'multiple_choice', 'multiple_choice_multi', 'yes_no'].includes(q.type) && (
                <div className="space-y-2">
                  <Label className="text-xs text-[#4A6B68]">Options & Scores</Label>
                  {q.options?.map((opt, oIdx) => (
                    <div key={oIdx} className="flex items-center gap-2">
                      <Input value={opt} onChange={e => updateOption(qIdx, oIdx, e.target.value)} className="flex-1 rounded-xl border-[#E5F2F1] text-sm" placeholder="Option text" />
                      <Input type="number" value={q.scores?.[oIdx] ?? 0} onChange={e => updateScore(qIdx, oIdx, e.target.value)} className="w-16 rounded-xl border-[#E5F2F1] text-sm text-center" placeholder="Score" />
                      {q.type !== 'yes_no' && (q.options?.length || 0) > 2 && (
                        <Button variant="ghost" size="sm" onClick={() => removeOption(qIdx, oIdx)} className="text-[#D3544E] h-7 px-1"><Trash2 className="w-3 h-3" /></Button>
                      )}
                    </div>
                  ))}
                  {q.type !== 'yes_no' && (
                    <Button variant="ghost" size="sm" onClick={() => addOption(qIdx)} className="text-[#0D8B7D] text-xs h-7" data-testid={`add-option-${qIdx}`}><Plus className="w-3 h-3 mr-1" />Add Option</Button>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        ))}

        {questions.length === 0 && (
          <Card className="bg-white border-[#E5F2F1] rounded-2xl border-dashed">
            <CardContent className="p-8 text-center text-[#4A6B68]">
              <FileSpreadsheet className="w-8 h-8 mx-auto mb-2 text-[#4A6B68]/40" />
              <p className="text-sm">No questions yet. Click "Add Questions" or "Upload CSV" to get started.</p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Scoring Ranges */}
      <Card className="bg-white border-[#E5F2F1] rounded-2xl">
        <CardContent className="p-6 space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-base font-semibold text-[#1A3330] font-['Outfit']">Scoring Interpretation</h3>
              {questions.length > 0 && (
                <p className="text-[10px] text-[#4A6B68] mt-0.5">Score range: {minPossible} — {maxPossible}</p>
              )}
            </div>
            <Button variant="ghost" size="sm" onClick={addRange} className="text-[#0D8B7D] text-xs h-7" data-testid="add-range-btn"><Plus className="w-3 h-3 mr-1" />Add Range</Button>
          </div>
          {scoringRanges.map((r, idx) => (
            <div key={idx} className="flex items-center gap-2 flex-wrap" data-testid={`scoring-range-${idx}`}>
              <Input type="number" value={r.min} onChange={e => updateRange(idx, 'min', e.target.value)} className="w-16 rounded-xl border-[#E5F2F1] text-sm text-center" placeholder="From" />
              <span className="text-xs text-[#4A6B68]">to</span>
              <Input type="number" value={r.max} onChange={e => updateRange(idx, 'max', e.target.value)} className="w-16 rounded-xl border-[#E5F2F1] text-sm text-center" placeholder="To" />
              <Input value={r.label} onChange={e => updateRange(idx, 'label', e.target.value)} className="flex-1 min-w-[120px] rounded-xl border-[#E5F2F1] text-sm" placeholder="e.g., Minimal, Mild..." />
              <Select value={r.color || 'green'} onValueChange={v => updateRange(idx, 'color', v)}>
                <SelectTrigger className="w-28 rounded-xl border-[#E5F2F1] h-9 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {RANGE_COLORS.map(c => <SelectItem key={c} value={c}><Badge className={`${COLOR_MAP[c]} text-[10px] border-0`}>{c}</Badge></SelectItem>)}
                </SelectContent>
              </Select>
              {scoringRanges.length > 1 && <Button variant="ghost" size="sm" onClick={() => removeRange(idx)} className="text-[#D3544E] h-7 px-1"><Trash2 className="w-3 h-3" /></Button>}
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Save */}
      <div className="flex justify-end gap-3">
        <Button variant="ghost" onClick={() => navigate('/questionnaires')} className="text-[#4A6B68]">Cancel</Button>
        <Button onClick={handleSave} disabled={saving} className="bg-[#0D8B7D] hover:bg-[#0B786C] text-white rounded-xl" data-testid="save-questionnaire-btn">
          <Save className="w-4 h-4 mr-2" />{saving ? 'Saving...' : isEdit ? 'Update Questionnaire' : 'Save Questionnaire'}
        </Button>
      </div>

      {/* Bulk Add Modal */}
      <Dialog open={showBulkAdd} onOpenChange={setShowBulkAdd}>
        <DialogContent className="max-w-sm bg-white rounded-2xl" data-testid="bulk-add-modal">
          <DialogHeader><DialogTitle className="font-['Outfit'] text-[#1A3330]">Add Questions</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label className="text-sm">Number of Questions</Label>
              <Input type="number" min={1} max={50} value={bulkCount} onChange={e => setBulkCount(parseInt(e.target.value) || 1)} className="rounded-xl border-[#E5F2F1] mt-1" data-testid="bulk-count-input" /></div>
            <div><Label className="text-sm">Question Type</Label>
              <Select value={bulkType} onValueChange={setBulkType}>
                <SelectTrigger className="rounded-xl border-[#E5F2F1] mt-1" data-testid="bulk-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>{QUESTION_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <Button onClick={generateBulk} className="w-full bg-[#0D8B7D] hover:bg-[#0B786C] text-white rounded-xl" data-testid="generate-template-btn">Generate Template</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* CSV Preview Modal */}
      <Dialog open={showCsvPreview} onOpenChange={setShowCsvPreview}>
        <DialogContent className="max-w-2xl bg-white rounded-2xl max-h-[80vh] overflow-y-auto" data-testid="csv-preview-modal">
          <DialogHeader><DialogTitle className="font-['Outfit'] text-[#1A3330]">CSV Preview</DialogTitle></DialogHeader>
          {csvPreview && (
            <div className="space-y-4">
              <p className="text-sm text-[#4A6B68]">{csvPreview.length} questions parsed</p>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead><tr className="bg-[#F4FAF9]">
                    <th className="px-2 py-2 text-left text-[#4A6B68]">#</th>
                    <th className="px-2 py-2 text-left text-[#4A6B68]">Question</th>
                    <th className="px-2 py-2 text-left text-[#4A6B68]">Type</th>
                    <th className="px-2 py-2 text-left text-[#4A6B68]">Options</th>
                    <th className="px-2 py-2 text-left text-[#4A6B68]">Scores</th>
                  </tr></thead>
                  <tbody>
                    {csvPreview.map((p, i) => (
                      <tr key={i} className="border-b border-[#E5F2F1]">
                        <td className="px-2 py-1.5 text-[#1A3330]">{p.num}</td>
                        <td className="px-2 py-1.5 text-[#1A3330] max-w-[200px] truncate">{p.question_text}</td>
                        <td className="px-2 py-1.5"><Badge className="bg-[#0D8B7D]/10 text-[#0D8B7D] text-[9px] border-0">{p.type}</Badge></td>
                        <td className="px-2 py-1.5 text-[#4A6B68]">{p.options.join(' | ')}</td>
                        <td className="px-2 py-1.5 text-[#4A6B68]">{p.scores.join(' | ')}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="flex gap-2">
                <Button onClick={confirmCsvImport} className="flex-1 bg-[#0D8B7D] hover:bg-[#0B786C] text-white rounded-xl" data-testid="confirm-csv-import-btn">Confirm Import</Button>
                <Button variant="outline" className="rounded-xl border-[#E5F2F1]" onClick={() => { setShowCsvPreview(false); setCsvPreview(null); }}>Cancel</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Post-CSV Scoring Interpretation Dialog */}
      <Dialog open={showScoringAfterCsv} onOpenChange={setShowScoringAfterCsv}>
        <DialogContent className="max-w-lg bg-white rounded-2xl" data-testid="scoring-interpretation-modal">
          <DialogHeader><DialogTitle className="font-['Outfit'] text-[#1A3330]">Scoring Interpretation</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-3">
              <div className="bg-[#F4FAF9] p-3 rounded-xl text-center"><p className="text-[10px] text-[#4A6B68]">Questions</p><p className="text-lg font-semibold text-[#1A3330]">{questions.length}</p></div>
              <div className="bg-[#F4FAF9] p-3 rounded-xl text-center"><p className="text-[10px] text-[#4A6B68]">Min Score</p><p className="text-lg font-semibold text-[#1A3330]">{minPossible}</p></div>
              <div className="bg-[#F4FAF9] p-3 rounded-xl text-center"><p className="text-[10px] text-[#4A6B68]">Max Score</p><p className="text-lg font-semibold text-[#1A3330]">{maxPossible}</p></div>
            </div>
            <Label className="text-xs text-[#4A6B68]">Define score ranges for interpretation:</Label>
            {scoringRanges.map((r, idx) => (
              <div key={idx} className="flex items-center gap-2">
                <Input type="number" value={r.min} onChange={e => updateRange(idx, 'min', e.target.value)} className="w-16 rounded-xl border-[#E5F2F1] text-sm text-center" placeholder="From" />
                <span className="text-xs text-[#4A6B68]">to</span>
                <Input type="number" value={r.max} onChange={e => updateRange(idx, 'max', e.target.value)} className="w-16 rounded-xl border-[#E5F2F1] text-sm text-center" placeholder="To" />
                <Input value={r.label} onChange={e => updateRange(idx, 'label', e.target.value)} className="flex-1 rounded-xl border-[#E5F2F1] text-sm" placeholder="Label" />
                <Select value={r.color || 'green'} onValueChange={v => updateRange(idx, 'color', v)}>
                  <SelectTrigger className="w-24 rounded-xl border-[#E5F2F1] h-8 text-[10px]"><SelectValue /></SelectTrigger>
                  <SelectContent>{RANGE_COLORS.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
                {scoringRanges.length > 1 && <Button variant="ghost" size="sm" onClick={() => removeRange(idx)} className="text-[#D3544E] h-7 px-1"><X className="w-3 h-3" /></Button>}
              </div>
            ))}
            <Button variant="ghost" size="sm" onClick={addRange} className="text-[#0D8B7D] text-xs"><Plus className="w-3 h-3 mr-1" />Add Range</Button>
            <Button onClick={() => setShowScoringAfterCsv(false)} className="w-full bg-[#0D8B7D] hover:bg-[#0B786C] text-white rounded-xl" data-testid="save-scoring-btn">Save Interpretation</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
